import * as THREE from 'three';

/**
 * Every surface in the diorama samples one 256x256 atlas, so the entire static
 * scene can share a single material. Draw calls, not triangles, are what hurt
 * on the hardware this targets.
 *
 * The atlas is drawn in code rather than shipped as a file: it keeps the app at
 * zero asset bytes, and tweaking the look is a matter of editing numbers.
 * Swapping in a hand-painted PNG later means replacing CanvasTexture with a
 * loaded image and nothing else.
 */

const ATLAS = 256;
const CELL = 64; // 4x4 grid of tiles

/**
 * Rolling-stock palette. Blue carries the train; cream is trim only. Going by
 * the general blue-and-white scheme of the MR-63 rather than matching any
 * specific paint reference.
 */
const MTL_BLUE = '#2456a4';
const MTL_BLUE_DARK = '#183c76';
const MTL_BLUE_LIGHT = '#3f74c4';
const MTL_CREAM = '#d9d6c8';
const MTL_GLASS = '#20242c';

export interface Tile {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

export type TileName =
  | 'wall'
  | 'wallAccent'
  | 'floor'
  | 'platform'
  | 'platformEdge'
  | 'trainSide'
  | 'trainDoor'
  | 'trainFront'
  | 'trainRoof'
  | 'dark'
  | 'bench'
  | 'sign';

/** Grid cell (column, row) for each tile. */
const LAYOUT: Record<TileName, [number, number]> = {
  wall: [0, 0],
  wallAccent: [1, 0],
  floor: [2, 0],
  platform: [3, 0],
  trainSide: [0, 1],
  trainDoor: [1, 1],
  trainFront: [2, 1],
  trainRoof: [3, 1],
  dark: [0, 2],
  bench: [1, 2],
  sign: [2, 2],
  platformEdge: [3, 2],
};

/** Deterministic noise, so the atlas is identical on every launch. */
function rng(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

type Ctx = CanvasRenderingContext2D;

/** Draws into a CELL-sized tile whose top-left is at (ox, oy). */
type Painter = (ctx: Ctx, ox: number, oy: number) => void;

function fill(ctx: Ctx, ox: number, oy: number, color: string) {
  ctx.fillStyle = color;
  ctx.fillRect(ox, oy, CELL, CELL);
}

function speckle(ctx: Ctx, ox: number, oy: number, seed: number, colors: string[], n: number) {
  const random = rng(seed);
  for (let i = 0; i < n; i++) {
    ctx.fillStyle = colors[Math.floor(random() * colors.length)];
    const size = 1 + Math.floor(random() * 2);
    ctx.fillRect(ox + Math.floor(random() * CELL), oy + Math.floor(random() * CELL), size, size);
  }
}

/** Rectangular wall tiles with grout, the way a tiled station reads. */
function tiled(ctx: Ctx, ox: number, oy: number, base: string, grout: string, seed: number) {
  fill(ctx, ox, oy, grout);
  const random = rng(seed);
  const tw = 16;
  const th = 8;
  for (let y = 0; y < CELL; y += th) {
    const offset = (y / th) % 2 === 0 ? 0 : tw / 2;
    for (let x = -tw; x < CELL; x += tw) {
      // Vary each tile slightly so a large wall does not look like wallpaper.
      const shade = 0.92 + random() * 0.16;
      ctx.fillStyle = shadeColor(base, shade);
      ctx.fillRect(ox + x + offset + 1, oy + y + 1, tw - 2, th - 2);
    }
  }
}

function shadeColor(hex: string, factor: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, Math.round(((n >> 16) & 255) * factor));
  const g = Math.min(255, Math.round(((n >> 8) & 255) * factor));
  const b = Math.min(255, Math.round((n & 255) * factor));
  return `rgb(${r},${g},${b})`;
}

const PAINTERS: Record<TileName, Painter> = {
  // Montreal stations are heavy on warm ceramic tile and concrete.
  wall: (ctx, ox, oy) => {
    tiled(ctx, ox, oy, '#c8bda6', '#8d8574', 1);
  },
  wallAccent: (ctx, ox, oy) => {
    tiled(ctx, ox, oy, '#2f6ea8', '#20496e', 2);
  },
  floor: (ctx, ox, oy) => {
    fill(ctx, ox, oy, '#4b4a47');
    speckle(ctx, ox, oy, 3, ['#565550', '#403f3c', '#525049'], 260);
  },
  platform: (ctx, ox, oy) => {
    fill(ctx, ox, oy, '#8a8578');
    speckle(ctx, ox, oy, 4, ['#948f81', '#7d786d', '#918c7e'], 220);
    // Paving joints.
    ctx.fillStyle = '#6f6a60';
    for (let i = 0; i <= CELL; i += 32) {
      ctx.fillRect(ox + i, oy, 1, CELL);
      ctx.fillRect(ox, oy + i, CELL, 1);
    }
  },
  platformEdge: (ctx, ox, oy) => {
    fill(ctx, ox, oy, '#8a8578');
    ctx.fillStyle = '#d8b135'; // tactile warning strip
    ctx.fillRect(ox, oy + 20, CELL, 24);
    ctx.fillStyle = '#b8942a';
    for (let x = 0; x < CELL; x += 8) ctx.fillRect(ox + x + 2, oy + 24, 4, 16);
  },
  // MR-63 livery. The body is blue, not white: cream appears only as a thin
  // band of trim above and below the windows, which is what keeps the train from
  // reading as a generic white commuter unit.
  trainSide: (ctx, ox, oy) => {
    fill(ctx, ox, oy, MTL_BLUE);
    // Cream trim under the roof line.
    ctx.fillStyle = MTL_CREAM;
    ctx.fillRect(ox, oy + 6, CELL, 4);
    // Window band, inset in its own cream surround.
    ctx.fillStyle = MTL_CREAM;
    ctx.fillRect(ox + 4, oy + 12, CELL - 8, 26);
    ctx.fillStyle = MTL_GLASS;
    ctx.fillRect(ox + 6, oy + 14, CELL - 12, 22);
    ctx.fillStyle = '#3d4757'; // glass highlight along the top edge
    ctx.fillRect(ox + 8, oy + 16, CELL - 16, 5);
    // Darker blue skirt, and a lighter waist line to break up the flat blue.
    ctx.fillStyle = MTL_BLUE_LIGHT;
    ctx.fillRect(ox, oy + 42, CELL, 2);
    ctx.fillStyle = MTL_BLUE_DARK;
    ctx.fillRect(ox, oy + 54, CELL, 10);
  },
  // One leaf, not a whole doorway: this tile maps onto a single sliding leaf,
  // and the seam down the middle of a closed door is the physical gap between
  // two of them. Drawing a seam here as well produced a phantom third division.
  // The window here has to line up with the band on trainSide once both are
  // mapped onto the geometry - a door is 2.6 tall against the car's 3.2, so the
  // same pixel row is a different height on each. These rows put the door's
  // glass level with the car's, which is most of what makes the doors look like
  // part of the train rather than stickers on it.
  trainDoor: (ctx, ox, oy) => {
    fill(ctx, ox, oy, MTL_BLUE);
    // Leaf edges, so a closed pair reads as two panels meeting.
    ctx.fillStyle = MTL_BLUE_DARK;
    ctx.fillRect(ox, oy, 3, CELL);
    ctx.fillRect(ox + CELL - 3, oy, 3, CELL);
    ctx.fillStyle = MTL_CREAM;
    ctx.fillRect(ox + 7, oy + 4, CELL - 14, 27);
    ctx.fillStyle = MTL_GLASS;
    ctx.fillRect(ox + 10, oy + 7, CELL - 20, 21);
    ctx.fillStyle = MTL_BLUE_DARK;
    ctx.fillRect(ox, oy + 54, CELL, 10);
  },
  trainFront: (ctx, ox, oy) => {
    fill(ctx, ox, oy, MTL_BLUE);
    ctx.fillStyle = MTL_CREAM;
    ctx.fillRect(ox + 6, oy + 8, CELL - 12, 30);
    ctx.fillStyle = MTL_GLASS;
    ctx.fillRect(ox + 8, oy + 10, CELL - 16, 26);
    ctx.fillStyle = MTL_BLUE_DARK;
    ctx.fillRect(ox, oy + 52, CELL, 12);
    ctx.fillStyle = '#ffe9a8'; // headlights
    ctx.fillRect(ox + 6, oy + 42, 8, 5);
    ctx.fillRect(ox + CELL - 14, oy + 42, 8, 5);
  },
  trainRoof: (ctx, ox, oy) => {
    fill(ctx, ox, oy, '#8d8f93');
    ctx.fillStyle = '#7b7d81';
    for (let x = 0; x < CELL; x += 6) ctx.fillRect(ox + x, oy, 3, CELL);
  },
  dark: (ctx, ox, oy) => {
    fill(ctx, ox, oy, '#15171c');
    speckle(ctx, ox, oy, 5, ['#1b1e24', '#101216'], 120);
  },
  bench: (ctx, ox, oy) => {
    fill(ctx, ox, oy, '#7a5334');
    ctx.fillStyle = '#6a4629';
    for (let y = 0; y < CELL; y += 12) ctx.fillRect(ox, oy + y, CELL, 2);
  },
  sign: (ctx, ox, oy) => {
    fill(ctx, ox, oy, '#123a63');
    ctx.fillStyle = '#e8ecf2';
    // Suggestion of lettering rather than actual text: at this texel density
    // real glyphs turn to mush anyway.
    ctx.fillRect(ox + 8, oy + 26, 48, 6);
    ctx.fillRect(ox + 8, oy + 38, 30, 4);
  },
};

export interface Atlas {
  texture: THREE.CanvasTexture;
  tile(name: TileName): Tile;
}

export function createAtlas(): Atlas {
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS;
  canvas.height = ATLAS;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context for the texture atlas');

  ctx.imageSmoothingEnabled = false;
  fill(ctx, 0, 0, '#ff00ff'); // anything magenta means a missing tile

  for (const name of Object.keys(PAINTERS) as TileName[]) {
    const [col, row] = LAYOUT[name];
    // Clip each painter to its own cell. Some of them deliberately draw beyond
    // their edges - `tiled` starts a row at -tw so a brick straddles the left
    // boundary - and without clipping that spills into the neighbouring tile.
    // It cost a while to find: wallAccent bled blue over the right edge of the
    // wall tile, which showed up as a blue stripe at every tile repeat.
    ctx.save();
    ctx.beginPath();
    ctx.rect(col * CELL, row * CELL, CELL, CELL);
    ctx.clip();
    PAINTERS[name](ctx, col * CELL, row * CELL);
    ctx.restore();
  }

  const texture = new THREE.CanvasTexture(canvas);
  // Chunky texels, and no mip chain to build or sample.
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  // NoColorSpace, deliberately. Marking it sRGB makes the GPU decode to linear
  // on sample, but our shader writes its result straight out without the
  // matching encode step - so the colours would land on screen much darker than
  // they were painted. Passing them through untouched means what is drawn into
  // this canvas is exactly what appears.
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;

  // Half a texel in from each edge. Without mipmaps bleeding is only possible
  // exactly on the boundary, but that is precisely where tiled UVs land.
  const inset = 0.5 / ATLAS;
  const tiles = {} as Record<TileName, Tile>;
  for (const name of Object.keys(LAYOUT) as TileName[]) {
    const [col, row] = LAYOUT[name];
    tiles[name] = {
      u0: (col * CELL) / ATLAS + inset,
      // Canvas y runs down, texture v runs up.
      v0: 1 - ((row + 1) * CELL) / ATLAS + inset,
      u1: ((col + 1) * CELL) / ATLAS - inset,
      v1: 1 - (row * CELL) / ATLAS - inset,
    };
  }

  return { texture, tile: (name) => tiles[name] };
}
